import { useState } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { ArrowRight, Calculator } from "lucide-react";
import SEOHead from "@/components/site/SEOHead";
import PartnershipCompare from "@/components/site/PartnershipCompare";

export default function PartnershipModels() {
  const [params] = useSearchParams();
  const initial = Number(params.get("r")) || 0;
  const [rInput, setRInput] = useState(initial || "");
  const [r, setR] = useState(initial);
  const name = params.get("name") || "";
  const type = params.get("type") || "";
  const stage = params.get("stage") || "";
  const rooms = Number(params.get("rooms")) || 0;

  return (
    <div data-testid="partnership-models-page">
      <SEOHead
        title="Compare Partnership Models — Fixed Rent, Management & Revenue Share | Happy Retreats"
        description="Compare three transparent ways to partner with Happy Retreats: guaranteed fixed rental, the management model with maximum upside, and minimum guarantee with revenue share."
      />
      <section className="bg-ink text-ivory pt-44 md:pt-52 pb-14 md:pb-20">
        <div className="container-x">
          <div className="overline text-ivory/70 animate-fade-up">Partner with Happy Retreats</div>
          <h1 className="display-1 mt-8 max-w-4xl animate-fade-up" style={{ animationDelay: "0.15s" }}>
            Three ways to <span className="italic text-clay">partner</span>. One transparent comparison.
          </h1>
          <p className="mt-8 max-w-2xl text-base md:text-lg text-ivory/80 font-light leading-relaxed animate-fade-up" style={{ animationDelay: "0.3s" }}>
            Enter your property's projected annual revenue — or arrive here from the revenue calculator — and see exactly what you'd earn under fixed rent, the management model, and minimum guarantee with revenue share.
          </p>
          <div className="mt-10 flex flex-wrap items-end gap-4 animate-fade-up" style={{ animationDelay: "0.4s" }}>
            <label className="block w-72">
              <span className="text-[11px] uppercase tracking-[0.14em] text-ivory/60 font-medium">Projected annual revenue (excl. taxes)</span>
              <div className="mt-1.5 flex items-center gap-2 border border-ivory/25 bg-white/5 px-3 py-3 focus-within:border-ivory transition-colors">
                <span className="text-sm text-ivory/60">₹</span>
                <input type="number" min="0" value={rInput} placeholder="e.g. 10000000"
                  onChange={(e) => setRInput(e.target.value === "" ? "" : Math.max(0, Number(e.target.value)))}
                  onKeyDown={(e) => { if (e.key === "Enter") setR(Number(rInput) || 0); }}
                  className="w-full bg-transparent outline-none text-sm text-ivory" data-testid="pm-revenue-input" />
              </div>
            </label>
            <button type="button" onClick={() => setR(Number(rInput) || 0)}
              className="bg-ivory text-ink px-6 py-3.5 text-[11px] uppercase tracking-[0.2em] font-medium inline-flex items-center gap-2 hover:bg-clay hover:text-white transition-colors"
              data-testid="pm-compare-btn">
              Compare models <ArrowRight className="w-4 h-4" />
            </button>
            <Link to="/revenue-calculator" className="text-xs text-ivory/60 underline underline-offset-4 inline-flex items-center gap-1.5 pb-3.5 hover:text-ivory transition-colors" data-testid="pm-calc-link">
              <Calculator className="w-3.5 h-3.5" /> Don't know your number? Use the revenue calculator
            </Link>
          </div>
        </div>
      </section>
      {r > 0 && <PartnershipCompare r={r} propertyName={name} propertyType={type} rooms={rooms} stage={stage} />}
    </div>
  );
}
